import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type MfaStatus = {
  loading: boolean;
  /** Tiene rol admin u organizer (requiere MFA) */
  requiresMfa: boolean;
  /** Tiene al menos un factor TOTP verificado */
  hasEnrolledFactor: boolean;
  /** AAL actual: aal1 (sin MFA) o aal2 (con MFA) */
  currentLevel: "aal1" | "aal2" | null;
  /** AAL al que puede llegar (si tiene factor verificado, aal2) */
  nextLevel: "aal1" | "aal2" | null;
  refresh: () => Promise<void>;
};

const PRIVILEGED_ROLES = ["admin", "organizer"] as const;

export function useMfaStatus(): MfaStatus {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [requiresMfa, setRequiresMfa] = useState(false);
  const [hasEnrolledFactor, setHasEnrolledFactor] = useState(false);
  const [currentLevel, setCurrentLevel] = useState<"aal1" | "aal2" | null>(null);
  const [nextLevel, setNextLevel] = useState<"aal1" | "aal2" | null>(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setLoading(false);
      setRequiresMfa(false);
      setHasEnrolledFactor(false);
      setCurrentLevel(null);
      setNextLevel(null);
      return;
    }
    setLoading(true);
    try {
      const [{ data: roles }, { data: aal }, { data: factors }] = await Promise.all([
        supabase.from("user_roles").select("role").eq("user_id", user.id),
        supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
        supabase.auth.mfa.listFactors(),
      ]);

      const userRoles = (roles ?? []).map((r) => r.role);
      const privileged = userRoles.some((r) => (PRIVILEGED_ROLES as readonly string[]).includes(r));
      setRequiresMfa(privileged);

      const totpFactors = factors?.totp ?? [];
      const verified = totpFactors.some((f) => f.status === "verified");
      setHasEnrolledFactor(verified);

      setCurrentLevel((aal?.currentLevel as "aal1" | "aal2" | null) ?? null);
      setNextLevel((aal?.nextLevel as "aal1" | "aal2" | null) ?? null);
    } catch (err) {
      console.error("[useMfaStatus] error", err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    refresh();
  }, [authLoading, refresh]);

  return { loading, requiresMfa, hasEnrolledFactor, currentLevel, nextLevel, refresh };
}
